import { useState, useEffect, useCallback } from "react"
import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    ChevronsUpDown,
    Check,
    Filter,
    Users,
    CheckCircle2,
    XCircle,
    Eye,
    Share2,
    RotateCcw,
    Download,
    Loader2,
    RefreshCw,
    Pencil,
    Search,
    Trash2
} from "lucide-react"
import * as XLSX from "xlsx"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from "@/components/ui/card"

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog"

import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription
} from "@/components/ui/alert-dialog"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from "@/components/ui/command"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import AttendanceImportAction from "./AttendanceManager"
import TakeAttendanceAction from "./TakeAttendance"
import { ShareRegisterDialog } from "./ShareRegisterDialog"
import ExportRegisterAction from "./ExportRegisterAction"
import { SubjectCombobox } from "./SubjectCombobox"
import { formatLectureTime, lectureTimeError } from "./lectureTime"

// ─────────────────────────────────────────────────────────────────────────
// Types — mirror the shapes returned by attendanceController.js's
// listMyRegisters / getRegisterDetail / getMyRegisterFilters / getRegisterSummary
// ─────────────────────────────────────────────────────────────────────────

type RegisterRow = {
    id: string
    classroom_id: string
    classroom_name: string | null
    subject_code: string
    subject_name: string | null
    lecture_date: string
    period: string | null
    start_time: string | null   // "HH:MM" 24h; null when the lecture has no time range
    end_time: string | null
    taken_by_name: string | null
    roster_count: number
    present_count: number
    absent_count: number
    synced_at: number | null
    shared?: boolean   // someone else's register, shared with me
    access?: "view" | "edit" | null   // what the share allows (shared rows only)
}

type StudentAbsences = { uid: string; eid: string | null; name: string | null; absences: number }

type Summary = {
    totalLectures: number
    totalRosterInstances: number
    totalAbsences: number
    overallPercent: number
    studentsWithAbsences: StudentAbsences[]
}

type RegisterDetail = {
    session: RegisterRow & { taken_by_eid: string }
    sharedWithMe?: boolean
    access?: "view" | "edit"
    readOnly?: boolean
    absentees: { student_uid: string; student_eid: string | null; student_name: string | null; status: string }[]
    presentees?: { student_uid: string | null; student_eid: string | null; student_name: string | null }[]
}

type RosterStudent = { uid: string; name?: string; eid?: string }
type ClassroomOption = { id: string; name: string }

const PAGE_SIZE = 10

// Import Attendance and Export (flat) predate the attendance policy (they know
// nothing of slots, no-subject registers or report columns), so they're hidden
// while that work goes on. The code stays; true brings both buttons back.
const SHOW_LEGACY_IMPORT_EXPORT = false

// Mirrors AttendanceStatus.js on the backend. Free text is accepted there too
// - anything outside this list is kept verbatim and shown as typed.
const STATUS_OPTIONS = [
    { value: "absent", label: "Absent" },
    { value: "leave", label: "On leave" },
    { value: "excused", label: "Excused" },
]

function statusLabel(status: string) {
    const known = STATUS_OPTIONS.find(o => o.value === String(status).toLowerCase())
    return (known ? known.label : status).toUpperCase()
}

// ─────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────

async function apiGet<T>(
    path: string,
    params: Record<string, string | number | null | undefined>,
    segment: "attendance" | "classrooms" = "attendance"
): Promise<T> {
    const query = Object.entries(params)
        .filter(([ , v ]) => v !== undefined && v !== null && v !== "")
        .map(([ k, v ]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")

    const url = `${environment.BASE_URL}/${segment}/${path}${query ? `?${query}` : ""}`
    const res = await fetch(url, { headers: apiHeaders() as HeadersInit })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

async function apiPost<T>(path: string, body: any): Promise<T> {
    const url = `${environment.BASE_URL}/attendance/${path}`
    const res = await fetch(url, {
        method: "POST",
        headers: apiHeaders() as HeadersInit,
        body: JSON.stringify(body),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

function formatDate(d: string) {
    try {
        return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    } catch {
        return d
    }
}

export default function AttendanceRegistry() {

    // ── filters ──────────────────────────────────────────────────────────
    const [ classRoomId, setClassRoomId ] = useState<string>("")
    const [ classRoomLabel, setClassRoomLabel ] = useState<string>("")
    const [ filterClassroomQuery, setFilterClassroomQuery ] = useState("")
    const [ filterClassroomResults, setFilterClassroomResults ] = useState<ClassroomOption[]>([])
    const [ filterSearchingClassrooms, setFilterSearchingClassrooms ] = useState(false)
    const [ subjectCode, setSubjectCode ] = useState<string>("")
    const [ dateFrom, setDateFrom ] = useState<string>("")
    const [ dateTo, setDateTo ] = useState<string>("")

    // ── list state ───────────────────────────────────────────────────────
    const [ registers, setRegisters ] = useState<RegisterRow[]>([])
    const [ total, setTotal ] = useState(0)
    const [ hasMore, setHasMore ] = useState(false)
    const [ page, setPage ] = useState(1)
    const [ loading, setLoading ] = useState(false)
    const [ error, setError ] = useState<string | null>(null)

    // ── summary state ────────────────────────────────────────────────────
    const [ summary, setSummary ] = useState<Summary | null>(null)

    // ── detail dialog ────────────────────────────────────────────────────
    const [ detailOpen, setDetailOpen ] = useState(false)
    const [ detailLoading, setDetailLoading ] = useState(false)
    const [ detail, setDetail ] = useState<RegisterDetail | null>(null)

    // ── edit mode (correcting a mistake) ────────────────────────────────
    const [ editMode, setEditMode ] = useState(false)
    const [ editRoster, setEditRoster ] = useState<RosterStudent[]>([])
    const [ editLoadingRoster, setEditLoadingRoster ] = useState(false)
    // true = present, false = absent, undefined = wasn't in this register
    // (registered after the lecture). Three states, not two.
    const [ editTicks, setEditTicks ] = useState<Record<string, boolean | undefined>>({})
    // Why a non-present student is not present, keyed the same way as the ticks.
    // Without this every status collapsed to "ABSENT" on screen, because the
    // tick alone can't distinguish a leave from a plain absence.
    const [ editStatuses, setEditStatuses ] = useState<Record<string, string>>({})
    const [ editRosterSearch, setEditRosterSearch ] = useState("")
    const [ savingEdit, setSavingEdit ] = useState(false)
    const [ editMsg, setEditMsg ] = useState<string | null>(null)
    const [ editMsgIsError, setEditMsgIsError ] = useState(false)

    // client-side — the whole roster is already loaded (no backend
    // pagination), this just narrows what's rendered
    const filteredEditRoster = editRosterSearch.trim()
        ? editRoster.filter(m => {
            const q = editRosterSearch.trim().toLowerCase()
            return (m.name || "").toLowerCase().includes(q) || (m.eid || "").toLowerCase().includes(q)
        })
        : editRoster

    // classroom/subject/date are editable too, in case THOSE were picked
    // wrong — separate from the roster ticks above
    const [ editClassroom, setEditClassroom ] = useState<ClassroomOption | null>(null)
    const [ editClassroomPickerOpen, setEditClassroomPickerOpen ] = useState(false)
    const [ editClassroomQuery, setEditClassroomQuery ] = useState("")
    const [ editClassroomResults, setEditClassroomResults ] = useState<ClassroomOption[]>([])
    const [ editSearchingClassrooms, setEditSearchingClassrooms ] = useState(false)
    const [ editSubjectCode, setEditSubjectCode ] = useState("")
    const [ editSubjectName, setEditSubjectName ] = useState<string | null>(null)
    const [ editLectureDate, setEditLectureDate ] = useState("")
    const [ editPeriod, setEditPeriod ] = useState("")
    const [ editStartTime, setEditStartTime ] = useState("")
    const [ editEndTime, setEditEndTime ] = useState("")
    const editTimeError = lectureTimeError(editStartTime, editEndTime)

    // ── per-row sync retry ───────────────────────────────────────────────
    const [ syncingRowId, setSyncingRowId ] = useState<string | null>(null)
    const [ deletingRowId, setDeletingRowId ] = useState<string | null>(null)
    const [ deleteTargetId, setDeleteTargetId ] = useState<string | null>(null)
    const [ shareTarget, setShareTarget ] = useState<{ id: string; title: string } | null>(null)

    // ── classroom search combobox ───────────────────────────────────────
    const [ classroomPickerOpen, setClassroomPickerOpen ] = useState(false)

    // ── export ───────────────────────────────────────────────────────────
    const [ exporting, setExporting ] = useState(false)

    // ── classroom filter — searches the full institute directory (not just
    // classrooms this faculty has already submitted registers for), same
    // pattern as the edit-mode classroom picker below and the Flutter app's
    // ClassroomPickerSheet. ──────────────────────────────────────────────
    useEffect(() => {
        if (!classroomPickerOpen) return
        const t = setTimeout(async () => {
            setFilterSearchingClassrooms(true)
            try {
                const res = await apiGet<{ success: true; classrooms: ClassroomOption[] }>(
                    "getClassRooms", { query: filterClassroomQuery, limit: 20 }, "classrooms"
                )
                setFilterClassroomResults(res.classrooms)
            } catch {
                setFilterClassroomResults([])
            } finally {
                setFilterSearchingClassrooms(false)
            }
        }, 300)
        return () => clearTimeout(t)
    }, [ filterClassroomQuery, classroomPickerOpen ])

    const loadRegisters = useCallback(async (targetPage: number) => {
        setLoading(true)
        setError(null)
        try {
            const res = await apiGet<{ success: true; registers: RegisterRow[]; total: number; hasMore: boolean }>(
                "listMyRegisters",
                { classRoomId, subjectCode, dateFrom, dateTo, page: targetPage, limit: PAGE_SIZE }
            )
            setRegisters(res.registers)
            setTotal(res.total)
            setHasMore(res.hasMore)
            setPage(targetPage)
        } catch (e: any) {
            setError(e.message || "Failed to load registers")
        } finally {
            setLoading(false)
        }
    }, [ classRoomId, subjectCode, dateFrom, dateTo ])

    const loadSummary = useCallback(async () => {
        try {
            const res = await apiGet<{ success: true } & Summary>("getRegisterSummary", {
                classRoomId, subjectCode, dateFrom, dateTo
            })
            setSummary(res)
        } catch {
            setSummary(null)
        }
    }, [ classRoomId, subjectCode, dateFrom, dateTo ])

    // re-fetch whenever a filter changes
    useEffect(() => {
        loadRegisters(1)
        loadSummary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ classRoomId, subjectCode, dateFrom, dateTo ])

    const openDetail = async (sessionId: string) => {
        setDetailOpen(true)
        setDetailLoading(true)
        setDetail(null)
        setEditMode(false)
        setEditRoster([])
        setEditTicks({})
        setEditRosterSearch("")
        setEditMsg(null)
        setEditClassroom(null)
        setEditClassroomQuery("")
        setEditSubjectCode("")
        setEditSubjectName(null)
        setEditLectureDate("")
        setEditPeriod("")
        setEditStartTime("")
        setEditEndTime("")
        try {
            const res = await apiGet<{ success: true } & RegisterDetail>("getRegisterDetail", { sessionId })
            setDetail(res)
        } catch {
            setDetail(null)
        } finally {
            setDetailLoading(false)
        }
    }

    const handleSyncRow = async (sessionId: string) => {
        setSyncingRowId(sessionId)
        try {
            await apiPost("syncRegister", { sessionId })
            await loadRegisters(page)
            await loadSummary()
        } catch (e: any) {
            setError(e.message || "Sync failed")
        } finally {
            setSyncingRowId(null)
        }
    }

    // Server re-verifies ownership (taken_by_eid) independently — a faculty
    // member can only ever see their own registers in this list in the
    // first place, but that isn't trusted as the only gate.
    const confirmDeleteRow = async () => {
        if (!deleteTargetId) return
        const sessionId = deleteTargetId
        setDeleteTargetId(null)
        setDeletingRowId(sessionId)
        try {
            await apiPost("deleteRegister", { sessionId })
            if (detail?.session.id === sessionId) setDetailOpen(false)
            await loadRegisters(page)
            await loadSummary()
        } catch (e: any) {
            setError(e.message || "Delete failed")
        } finally {
            setDeletingRowId(null)
        }
    }

    // Loads a classroom's roster into the edit checklist. `preserveAbsentees`
    // pre-ticks based on the register's actual recorded absentees (initial
    // edit load); when the classroom itself is being CHANGED mid-edit, that
    // history belongs to a different classroom's students entirely, so it
    // defaults everyone to present instead.
    const loadEditRoster = async (classRoomIdToLoad: string, preserveAbsentees: boolean) => {
        setEditLoadingRoster(true)
        setEditRosterSearch("")
        try {
            const res = await apiGet<{ success: true; members: RosterStudent[] }>(
                "getClassRoomMembers", { classRoomId: classRoomIdToLoad }, "classrooms"
            )
            // The register's own recorded students come first, then anyone
            // currently registered who isn't already there. Using only the
            // current registery loses the whole register the moment a student
            // is unregistered or the classroom is cleared for a new batch —
            // the Postgres rows survive, but nothing was displaying them.
            let members: RosterStudent[] = res.members
            if (preserveAbsentees && detail) {
                const recorded = [
                    ...(detail.presentees || []),
                    ...(detail.absentees || []),
                ]
                const seen = new Set(members.map(m => m.eid).filter(Boolean))
                const extra = recorded
                    .filter(r => r.student_eid && !seen.has(r.student_eid))
                    .map(r => ({
                        uid: r.student_uid || r.student_eid!,
                        eid: r.student_eid!,
                        name: r.student_name || undefined,
                    }))
                members = [ ...members, ...extra ]
            }
            setEditRoster(members)
            // Built from what the register actually recorded, never inferred as
            // "not absent ⇒ present". A student registered into the classroom
            // after this lecture has no presence and no exception row, so they
            // stay undefined — shown unmarked for faculty to decide rather than
            // silently counted present at a lecture that predates them.
            const absentEidSet = preserveAbsentees && detail
                ? new Set(detail.absentees.map(a => a.student_eid).filter(Boolean))
                : new Set<string>()
            const presentEidSet = preserveAbsentees && detail
                ? new Set((detail.presentees || []).map(p => p.student_eid).filter(Boolean))
                : new Set<string>()

            // A server that predates the presentees field omits it entirely.
            // Without this check every student who was present would read as
            // "not in register", since they appear in neither list. Absent
            // field => fall back to the old not-absent-means-present rule.
            const hasPresenteeData = !detail || Array.isArray(detail.presentees)

            const t: Record<string, boolean | undefined> = {}
            members.forEach(m => {
                if (!preserveAbsentees) { t[ m.uid ] = true; return }   // classroom changed — fresh register
                if (!hasPresenteeData) { t[ m.uid ] = !(m.eid && absentEidSet.has(m.eid)); return }
                if (m.eid && presentEidSet.has(m.eid)) t[ m.uid ] = true
                else if (m.eid && absentEidSet.has(m.eid)) t[ m.uid ] = false
                else t[ m.uid ] = undefined                            // not in this register
            })
            setEditTicks(t)

            const st: Record<string, string> = {}
            if (preserveAbsentees && detail) {
                const byEid = new Map((detail.absentees || []).map(a => [ a.student_eid, a.status ]))
                members.forEach(m => {
                    const recorded = m.eid ? byEid.get(m.eid) : null
                    if (recorded) st[ m.uid ] = recorded
                })
            }
            setEditStatuses(st)
        } catch (e: any) {
            setEditMsgIsError(true)
            setEditMsg(e.message || "Failed to load roster for editing")
        } finally {
            setEditLoadingRoster(false)
        }
    }

    const startEdit = async () => {
        if (!detail) return
        setEditMode(true)
        setEditMsg(null)
        setEditClassroom({ id: detail.session.classroom_id, name: detail.session.classroom_name || detail.session.classroom_id })
        setEditSubjectCode(detail.session.subject_code)
        setEditSubjectName(detail.session.subject_name)
        setEditLectureDate(detail.session.lecture_date)
        setEditPeriod(detail.session.period || "")
        setEditStartTime(detail.session.start_time || "")
        setEditEndTime(detail.session.end_time || "")
        await loadEditRoster(detail.session.classroom_id, true)
    }

    // debounced classroom search, edit mode only
    useEffect(() => {
        if (!editMode || !editClassroomPickerOpen) return
        const t = setTimeout(async () => {
            setEditSearchingClassrooms(true)
            try {
                const res = await apiGet<{ success: true; classrooms: ClassroomOption[] }>(
                    "getClassRooms", { query: editClassroomQuery, limit: 20 }, "classrooms"
                )
                setEditClassroomResults(res.classrooms)
            } catch {
                setEditClassroomResults([])
            } finally {
                setEditSearchingClassrooms(false)
            }
        }, 300)
        return () => clearTimeout(t)
    }, [ editClassroomQuery, editMode, editClassroomPickerOpen ])

    const selectEditClassroom = async (c: ClassroomOption) => {
        setEditClassroom(c)
        setEditClassroomPickerOpen(false)
        await loadEditRoster(c.id, false)
    }

    const saveEdit = async () => {
        if (!detail || !editClassroom) return
        // Unmarked students are sent in neither list — they weren't part of
        // this lecture. The backend scopes the roster to the lecture date
        // anyway, so they'd be ignored regardless; not sending them keeps the
        // request honest about what was actually recorded.
        const present = editRoster.filter(m => editTicks[ m.uid ] === true).map(m => ({ uid: m.uid, eid: m.eid, name: m.name }))
        const absent = editRoster.filter(m => editTicks[ m.uid ] === false).map(m => ({
            uid: m.uid, eid: m.eid, name: m.name,
            status: editStatuses[ m.uid ] || "absent",
        }))

        setSavingEdit(true)
        setEditMsg(null)
        try {
            const res = await apiPost<{ success: true; presentCount: number; absentCount: number; changed: number; subjectChanged: boolean; synced: boolean }>(
                "updateRegister", {
                    sessionId: detail.session.id,
                    classRoomId: editClassroom.id,
                    subjectCode: editSubjectCode,
                    subjectName: editSubjectName,
                    lectureDate: editLectureDate,
                    period: editPeriod.trim(),          // always sent: blank clears the stored value
                    startTime: editStartTime || null,
                    endTime: editEndTime || null,
                    present,
                    absent,
                }
            )
            setEditMsgIsError(false)
            setEditMsg(`Saved — ${res.changed} student(s) corrected.${res.subjectChanged ? " Subject changed, counters moved to the new subject." : ""}${res.synced ? "" : " (sync pending)"}`)
            await openDetail(detail.session.id)
            await loadRegisters(page)
            await loadSummary()
        } catch (e: any) {
            setEditMsgIsError(true)
            setEditMsg(e.message || "Failed to save changes")
        } finally {
            setSavingEdit(false)
        }
    }

    const clearFilters = () => {
        setClassRoomId("")
        setSubjectCode("")
        setDateFrom("")
        setDateTo("")
    }

    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await apiGet<{
                success: true
                registers: (RegisterRow & { period: string | null })[]
                absences: { lecture_date: string; period: string | null; start_time: string | null; end_time: string | null; subject_code: string; subject_name: string | null; classroom_id: string; classroom_name: string | null; student_name: string | null; student_eid: string | null }[]
                truncated: boolean
            }>("getExportData", { classRoomId, subjectCode, dateFrom, dateTo })

            const wb = XLSX.utils.book_new()

            const registerRows = res.registers.map(r => ({
                Date: r.lecture_date,
                Time: formatLectureTime(r.start_time, r.end_time),
                Period: r.period || "",
                Subject: r.subject_name || r.subject_code,
                Classroom: r.classroom_name || r.classroom_id,
                Roster: r.roster_count,
                Present: r.present_count,
                Absent: r.absent_count,
                Synced: r.synced_at ? "Yes" : "Pending",
            }))
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(registerRows), "Registers")

            const absenceRows = res.absences.map(a => ({
                Date: a.lecture_date,
                Time: formatLectureTime(a.start_time, a.end_time),
                Period: a.period || "",
                Subject: a.subject_name || a.subject_code,
                Classroom: a.classroom_name || a.classroom_id,
                Student: a.student_name || "—",
                "Student ID": a.student_eid || "—",
            }))
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(absenceRows), "Absences")

            XLSX.writeFile(wb, `attendance_registry_${new Date().toISOString().slice(0, 10)}.xlsx`)

            if (res.truncated) {
                alert("This export was capped at 2000 registers — narrow your filters for a complete export.")
            }
        } catch (e: any) {
            alert(e.message || "Export failed")
        } finally {
            setExporting(false)
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8">

            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">
                        Attendance Registry
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Lectures you've taken attendance for — filter, review, and see accurate per-student attendance.
                    </p>
                </div>

                <div className="flex gap-2">
                    <TakeAttendanceAction onSubmitted={() => { loadRegisters(page); loadSummary() }} onOpenRegister={openDetail} />
                    {SHOW_LEGACY_IMPORT_EXPORT && <AttendanceImportAction onImported={() => { loadRegisters(page); loadSummary() }} />}
                    <ExportRegisterAction />
                    {SHOW_LEGACY_IMPORT_EXPORT && (
                        <Button variant="outline" className="gap-2" onClick={handleExport} disabled={exporting}>
                            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            Export (flat)
                        </Button>
                    )}
                </div>
            </div>

            {/* FILTERS */}
            <Card className="mb-6 border border-border/60 shadow-sm">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="h-4 w-4 text-primary" />
                        <span className="text-xs font-semibold uppercase tracking-widest text-primary">Filters</span>
                        <Separator className="flex-1" />
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-xs">
                            <RotateCcw className="h-3 w-3" /> Clear
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label>Classroom</Label>
                            <Popover open={classroomPickerOpen} onOpenChange={setClassroomPickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={classroomPickerOpen}
                                        className="w-full justify-between font-normal"
                                    >
                                        {classRoomId ? classRoomLabel || classRoomId : "All classrooms"}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search classroom..."
                                            value={filterClassroomQuery}
                                            onValueChange={setFilterClassroomQuery}
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                {filterSearchingClassrooms ? "Searching..." : "No classroom found."}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem
                                                    value="__all"
                                                    onSelect={() => { setClassRoomId(""); setClassRoomLabel(""); setClassroomPickerOpen(false) }}
                                                >
                                                    <Check className={cn("mr-2 h-4 w-4", classRoomId === "" ? "opacity-100" : "opacity-0")} />
                                                    All classrooms
                                                </CommandItem>
                                                {filterClassroomResults.map(c => (
                                                    <CommandItem
                                                        key={c.id}
                                                        value={c.id}
                                                        onSelect={() => {
                                                            setClassRoomId(c.id)
                                                            setClassRoomLabel(c.name || c.id)
                                                            setClassroomPickerOpen(false)
                                                        }}
                                                    >
                                                        <Check className={cn("mr-2 h-4 w-4", classRoomId === c.id ? "opacity-100" : "opacity-0")} />
                                                        {c.name || c.id} <span className="ml-2 text-xs text-muted-foreground">{c.id}</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label>Subject</Label>
                            <SubjectCombobox
                                value={subjectCode}
                                placeholder="All subjects"
                                allowClear
                                onClear={() => setSubjectCode("")}
                                onSelect={(s) => setSubjectCode(s.subject_code)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>From date</Label>
                            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                        </div>

                        <div className="space-y-2">
                            <Label>To date <span className="text-muted-foreground font-normal">(same as From = single day)</span></Label>
                            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                        </div>
                    </div>

                </CardContent>
            </Card>

            {/* SUMMARY */}
            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card className="border border-border/60 shadow-sm">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
                                <CalendarDays className="h-3.5 w-3.5" /> Lectures taken
                            </div>
                            <div className="text-2xl font-bold mt-1">{summary.totalLectures}</div>
                        </CardContent>
                    </Card>
                    <Card className="border border-border/60 shadow-sm">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
                                <Users className="h-3.5 w-3.5" /> Roster instances
                            </div>
                            <div className="text-2xl font-bold mt-1">{summary.totalRosterInstances}</div>
                        </CardContent>
                    </Card>
                    <Card className="border border-border/60 shadow-sm">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
                                <XCircle className="h-3.5 w-3.5" /> Total absences
                            </div>
                            <div className="text-2xl font-bold mt-1 text-red-600">{summary.totalAbsences}</div>
                        </CardContent>
                    </Card>
                    <Card className="border border-border/60 shadow-sm">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Overall attendance
                            </div>
                            <div className={`text-2xl font-bold mt-1 ${summary.overallPercent >= 75 ? "text-emerald-600" : "text-red-600"}`}>
                                {summary.overallPercent}%
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* REGISTER LIST */}
            <Card className="border border-border/60 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-base font-semibold">Registers</CardTitle>
                    <CardDescription>{total} register{total === 1 ? "" : "s"} matching current filters</CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <p className="text-sm text-red-600 mb-4">{error}</p>
                    )}

                    <div className="rounded-lg border border-border/60 overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Subject</TableHead>
                                    <TableHead>Classroom</TableHead>
                                    <TableHead>Present</TableHead>
                                    <TableHead>Absent</TableHead>
                                    <TableHead>Synced</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                                            Loading…
                                        </TableCell>
                                    </TableRow>
                                )}

                                {!loading && registers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                                            No registers match these filters yet.
                                        </TableCell>
                                    </TableRow>
                                )}

                                {!loading && registers.map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell className="font-medium">{formatDate(r.lecture_date)}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                            {formatLectureTime(r.start_time, r.end_time) || "—"}
                                            {r.period ? <span className="block text-xs">Period {r.period}</span> : null}
                                        </TableCell>
                                        <TableCell>
                                            {r.subject_name || r.subject_code}
                                            {r.shared && <Badge variant="outline" className="ml-2 border-sky-300 bg-sky-50 text-[10px] text-sky-700">Shared by {r.taken_by_name || "a colleague"} · {r.access === "edit" ? "Can edit" : "View only"}</Badge>}
                                        </TableCell>
                                        <TableCell>{r.classroom_name || r.classroom_id}</TableCell>
                                        <TableCell className="text-emerald-600 font-medium">{r.present_count}</TableCell>
                                        <TableCell className="text-red-600 font-medium">{r.absent_count}</TableCell>
                                        <TableCell>
                                            {r.synced_at ? (
                                                <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">Synced</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200">Pending</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                {!r.synced_at && !r.shared && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1"
                                                        disabled={syncingRowId === r.id}
                                                        onClick={() => handleSyncRow(r.id)}
                                                    >
                                                        {syncingRowId === r.id
                                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            : <RefreshCw className="h-3.5 w-3.5" />}
                                                        Sync
                                                    </Button>
                                                )}
                                                <Button variant="outline" size="sm" className="gap-1" onClick={() => openDetail(r.id)}>
                                                    <Eye className="h-3.5 w-3.5" /> View
                                                </Button>
                                                {!r.shared && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="gap-1 text-red-600 hover:text-red-700"
                                                    disabled={deletingRowId === r.id}
                                                    onClick={() => setDeleteTargetId(r.id)}
                                                >
                                                    {deletingRowId === r.id
                                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        : <Trash2 className="h-3.5 w-3.5" />}
                                                </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {/* DELETE CONFIRMATION */}
                    <AlertDialog open={!!deleteTargetId} onOpenChange={(next) => !next && setDeleteTargetId(null)}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this register?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This removes the register permanently. If it was already synced, its counts
                                    will be reversed out of student attendance first. This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <Button variant="outline" onClick={() => setDeleteTargetId(null)}>Cancel</Button>
                                <Button variant="destructive" onClick={confirmDeleteRow}>Delete Register</Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    {/* PAGINATION */}
                    <div className="flex items-center justify-between mt-4">
                        <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page <= 1 || loading}
                                onClick={() => loadRegisters(page - 1)}
                                className="gap-1"
                            >
                                <ChevronLeft className="h-3.5 w-3.5" /> Prev
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={!hasMore || loading}
                                onClick={() => loadRegisters(page + 1)}
                                className="gap-1"
                            >
                                Next <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <ShareRegisterDialog sessionId={shareTarget?.id ?? null} title={shareTarget?.title ?? ""} onClose={() => setShareTarget(null)} />

            {/* REGISTER DETAIL DIALOG */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <div className="flex items-center justify-between pr-6">
                            <DialogTitle className="text-xl font-bold">
                                {editMode ? "Correct register" : "Register detail"}
                            </DialogTitle>
                            {/* the creator: share / edit / delete · shared for editing: edit only */}
                            {!editMode && detail && !detail.readOnly && (
                                <div className="flex gap-2">
                                    {!detail.sharedWithMe && (
                                    <Button variant="outline" size="sm" className="gap-1"
                                        onClick={() => setShareTarget({ id: detail.session.id, title: `${detail.session.subject_name || detail.session.subject_code} · ${formatDate(detail.session.lecture_date)}` })}>
                                        <Share2 className="h-3.5 w-3.5" /> Share
                                    </Button>
                                    )}
                                    <Button variant="outline" size="sm" className="gap-1" onClick={startEdit}>
                                        <Pencil className="h-3.5 w-3.5" /> Edit
                                    </Button>
                                    {!detail.sharedWithMe && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-1 text-red-600 hover:text-red-700"
                                        onClick={() => setDeleteTargetId(detail.session.id)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </DialogHeader>

                    {detailLoading && (
                        <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
                    )}

                    {!detailLoading && detail && (
                        <div className="space-y-4">
                            {detail.sharedWithMe && (
                                <p className="rounded-md border border-sky-200 bg-sky-50 p-2 text-sm text-sky-800">
                                    Shared with you by {detail.session.taken_by_name || "a colleague"} — {detail.access === "edit" ? "you can edit it; only they can delete or share it." : "view only."}
                                </p>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                <div>
                                    <p className="text-muted-foreground text-xs mb-1">Date</p>
                                    {editMode ? (
                                        <Input
                                            type="date"
                                            value={editLectureDate}
                                            onChange={e => setEditLectureDate(e.target.value)}
                                            className="h-8 text-sm"
                                        />
                                    ) : (
                                        <p className="font-medium">{formatDate(detail.session.lecture_date)}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs mb-1">Time</p>
                                    {editMode ? (
                                        <div className="flex items-center gap-1">
                                            <Input type="time" value={editStartTime} onChange={e => setEditStartTime(e.target.value)} className="h-8 text-sm" />
                                            <span className="text-muted-foreground">–</span>
                                            <Input type="time" value={editEndTime} onChange={e => setEditEndTime(e.target.value)} className="h-8 text-sm" />
                                        </div>
                                    ) : (
                                        <p className="font-medium">{formatLectureTime(detail.session.start_time, detail.session.end_time) || "—"}</p>
                                    )}
                                    {editMode && editTimeError && <p className="text-xs text-red-600 mt-1">{editTimeError}</p>}
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs mb-1">Period</p>
                                    {editMode ? (
                                        <Input value={editPeriod} onChange={e => setEditPeriod(e.target.value)} placeholder="optional" className="h-8 text-sm" />
                                    ) : (
                                        <p className="font-medium">{detail.session.period || "—"}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs mb-1">Subject</p>
                                    {editMode ? (
                                        <SubjectCombobox
                                            value={editSubjectCode.startsWith("__class_") ? `${editSubjectName || "No subject"} (no subject)` : editSubjectCode}
                                            onSelect={(s) => {
                                                setEditSubjectCode(s.subject_code)
                                                setEditSubjectName(s.subject_name)
                                            }}
                                        />
                                    ) : (
                                        <p className="font-medium">{detail.session.subject_name || detail.session.subject_code}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs mb-1">Classroom</p>
                                    {editMode ? (
                                        <Popover open={editClassroomPickerOpen} onOpenChange={setEditClassroomPickerOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className="w-full justify-between font-normal h-8 text-sm"
                                                >
                                                    {editClassroom ? editClassroom.name : "Search classroom..."}
                                                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                                <Command shouldFilter={false}>
                                                    <CommandInput
                                                        placeholder="Search classroom..."
                                                        value={editClassroomQuery}
                                                        onValueChange={setEditClassroomQuery}
                                                    />
                                                    <CommandList>
                                                        <CommandEmpty>
                                                            {editSearchingClassrooms ? "Searching..." : "No classroom found."}
                                                        </CommandEmpty>
                                                        <CommandGroup>
                                                            {editClassroomResults.map(c => (
                                                                <CommandItem key={c.id} value={c.id} onSelect={() => selectEditClassroom(c)}>
                                                                    {c.name} <span className="ml-2 text-xs text-muted-foreground">{c.id}</span>
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    ) : (
                                        <p className="font-medium">{detail.session.classroom_name || detail.session.classroom_id}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs">Roster</p>
                                    <p className="font-medium">{detail.session.roster_count} students</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs">Present</p>
                                    <p className="font-medium text-emerald-600">{detail.session.present_count}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs">Absent</p>
                                    <p className="font-medium text-red-600">{detail.session.absent_count}</p>
                                </div>
                            </div>

                            {editMode && (
                                <p className="text-xs text-muted-foreground -mt-2">
                                    Changing classroom reloads its roster below (previous present/absent marks won't carry over — different students). Changing subject moves this lecture's counters to the new subject on save.
                                </p>
                            )}

                            <Separator />

                            {!editMode ? (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">
                                        Not marked present ({detail.absentees.length})
                                    </p>
                                    {detail.absentees.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">Everyone on this page was marked present.</p>
                                    ) : (
                                        <div className="rounded-lg border border-border/60 overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Student</TableHead>
                                                        <TableHead>ID</TableHead>
                                                        <TableHead>Status</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {detail.absentees.map(a => (
                                                        <TableRow key={a.student_eid || a.student_uid}>
                                                            <TableCell className="font-medium">{a.student_name || a.student_uid}</TableCell>
                                                            <TableCell>{a.student_eid || "—"}</TableCell>
                                                            <TableCell>{statusLabel(a.status)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {editLoadingRoster && (
                                        <p className="text-sm text-muted-foreground text-center py-6">Loading roster…</p>
                                    )}

                                    {!editLoadingRoster && editRoster.length > 0 && (
                                        <>
                                            <div className="flex items-center justify-between gap-2">
                                                <Label className="shrink-0">Tap a student to correct present/absent</Label>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        const t: Record<string, boolean> = {}
                                                        editRoster.forEach(m => { t[ m.uid ] = true })
                                                        setEditTicks(t)
                                                    }}
                                                >
                                                    Check all present
                                                </Button>
                                            </div>
                                            <div className="relative">
                                                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                                <Input
                                                    placeholder="Find by name or ID..."
                                                    value={editRosterSearch}
                                                    onChange={e => setEditRosterSearch(e.target.value)}
                                                    className="pl-8 h-9 text-sm"
                                                />
                                            </div>
                                            {editRosterSearch.trim() && (
                                                <p className="text-xs text-muted-foreground">{filteredEditRoster.length} of {editRoster.length} shown</p>
                                            )}
                                            <div className="rounded-lg border border-border/60 max-h-64 overflow-y-auto divide-y divide-border/40">
                                                {filteredEditRoster.length === 0 && (
                                                    <p className="text-sm text-muted-foreground text-center py-6">No student matches "{editRosterSearch}".</p>
                                                )}
                                                {filteredEditRoster.map(m => {
                                                    const present = editTicks[ m.uid ]
                                                    return (
                                                        <div key={m.uid} className="w-full flex items-center hover:bg-muted/40 transition-colors">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditTicks(t => ({
                                                                // undefined → present → absent → present …
                                                                // First tap on an unmarked student adds them to
                                                                // this register; there is no way back to unmarked,
                                                                // which is deliberate — once faculty decide, the
                                                                // student is part of the lecture.
                                                                ...t,
                                                                [ m.uid ]: t[ m.uid ] === undefined ? true : !t[ m.uid ],
                                                            }))}
                                                            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
                                                        >
                                                            <span className="text-sm font-medium">
                                                                {m.name || m.uid}
                                                                {m.eid && <span className="ml-2 text-xs text-muted-foreground font-normal">({m.eid})</span>}
                                                            </span>
                                                            <Badge
                                                                variant="outline"
                                                                className={present === undefined
                                                                    ? "text-muted-foreground bg-muted border-border"
                                                                    : present
                                                                        ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                                                                        : "text-red-600 bg-red-50 border-red-200"}
                                                                title={present === undefined
                                                                    ? "Registered after this lecture — not part of this register"
                                                                    : undefined}
                                                            >
                                                                {/* The recorded status, not a flat "ABSENT" — a leave or a typed
                                                                    reason has to survive a round trip through the edit view. */}
                                                                {present === undefined
                                                                    ? "NOT IN REGISTER"
                                                                    : present
                                                                        ? "PRESENT"
                                                                        : statusLabel(editStatuses[ m.uid ] || "absent")}
                                                            </Badge>
                                                        </button>
                                                        {present === false && (
                                                            <select
                                                                value={STATUS_OPTIONS.some(o => o.value === (editStatuses[ m.uid ] || "absent"))
                                                                    ? (editStatuses[ m.uid ] || "absent")
                                                                    : "__custom__"}
                                                                onChange={e => {
                                                                    const v = e.target.value
                                                                    if (v === "__custom__") {
                                                                        const typed = window.prompt("Reason for this student", editStatuses[ m.uid ] || "")
                                                                        if (typed && typed.trim()) {
                                                                            setEditStatuses(st => ({ ...st, [ m.uid ]: typed.trim() }))
                                                                        }
                                                                        return
                                                                    }
                                                                    setEditStatuses(st => ({ ...st, [ m.uid ]: v }))
                                                                }}
                                                                className="shrink-0 h-7 text-xs rounded-md border border-border bg-background px-2 mr-4"
                                                            >
                                                                {STATUS_OPTIONS.map(o => (
                                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                                ))}
                                                                <option value="__custom__">Other</option>
                                                            </select>
                                                        )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}

                                    {editMsg && (
                                        <p className={`text-sm ${editMsgIsError ? "text-red-600" : "text-emerald-600"}`}>
                                            {editMsg}
                                        </p>
                                    )}

                                    <DialogFooter className="gap-2 pt-2">
                                        <Button variant="outline" onClick={() => setEditMode(false)} disabled={savingEdit}>
                                            Cancel
                                        </Button>
                                        <Button
                                            className="min-w-[160px] gap-2"
                                            onClick={saveEdit}
                                            disabled={savingEdit || editLoadingRoster || editRoster.length === 0 || !editClassroom || !editSubjectCode || !editLectureDate || !!editTimeError}
                                        >
                                            {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                            Save Changes
                                        </Button>
                                    </DialogFooter>
                                </div>
                            )}
                        </div>
                    )}

                    {!detailLoading && !detail && (
                        <p className="text-sm text-red-600 py-6 text-center">Couldn't load this register.</p>
                    )}
                </DialogContent>
            </Dialog>

        </div>
    )
}
